import {EyeIcon} from "@/components/shared";
import {Popover, PopoverContent, PopoverTrigger} from "@nextui-org/react";
import {useState} from "react";
import {FaBusAlt} from "react-icons/fa";
import {FaSchoolCircleExclamation} from "react-icons/fa6";
import {RiDoorOpenLine} from "react-icons/ri";
import {RxExit} from "react-icons/rx";
import {TbDoorEnter} from "react-icons/tb";
import Loader from "../Loader";

interface AttendanceInconsistencyProps {
  totalInconsistencies: number;
  affectedStudents: number;
  totalStudents: number;
  noSchoolArrival: number;
  noSchoolEntry: number;
  noBusPickup: number;
  pickupDropMismatchCount: number;
  noSchoolExit: number;
  noReturnPickup: number;
  noReturnDrop: number;
}

interface AttendanceInconsistencywidgetProps {
  attendanceInconsistency: AttendanceInconsistencyProps;
  isLoading: boolean;
  instituteuserids: any;
  openPopup: (title: popupParams) => void;
}

const AttendanceInconsistencySummarywidget: React.FC<
  AttendanceInconsistencywidgetProps
> = ({isLoading, attendanceInconsistency, openPopup}) => {
  const getPercentage = (count: number) => {
    if (!attendanceInconsistency.totalStudents || !count) {
      return 0;
    }

    return parseFloat(
      ((count / attendanceInconsistency.totalStudents) * 100).toFixed(2)
    );
  };
  const [isPopoverOpen, setIsPopOverOpen] = useState(false);

  const detailCards = [
    {
      key: "arrival",
      titleTop: "Boarded Bus -",
      titleBottom: "Not Deboarded at School",
      inconsistencyType: 1,
      count: attendanceInconsistency.noSchoolArrival,
      percentage: getPercentage(attendanceInconsistency.noSchoolArrival),
      tagColor: "#2563EB",
      tagBg: "#DBEAFE",
      iconColor: "#1D4ED8",
      icon: <FaBusAlt className="h-4 w-4" />
    },
    {
      key: "entry",
      titleTop: "Deboarded at School –",
      titleBottom: "No School Entry",
      inconsistencyType: 2,
      count: attendanceInconsistency.noSchoolEntry,
      percentage: getPercentage(attendanceInconsistency.noSchoolEntry),
      tagColor: "#F97316",
      tagBg: "#FFEDD5",
      iconColor: "#EA580C",
      icon: <RiDoorOpenLine className="h-4 w-4" />
    },
    {
      key: "pickup",
      titleTop: "School Entry Found –",
      titleBottom: "No Morning Bus Record",
      inconsistencyType: 3,
      count: attendanceInconsistency.noBusPickup,
      percentage: getPercentage(attendanceInconsistency.noBusPickup),
      tagColor: "#22A447",
      tagBg: "#DCFCE7",
      iconColor: "#15803D",
      icon: <TbDoorEnter className="h-4 w-4" />
    },
    {
      key: "mismatch",
      titleTop: "Entered School –",
      titleBottom: "No School Exit",
      inconsistencyType: 5,
      count: attendanceInconsistency.noSchoolExit,
      percentage: getPercentage(attendanceInconsistency.noSchoolExit),
      tagColor: "#7C3AED",
      tagBg: "#EDE9FE",
      iconColor: "#6D28D9",
      icon: <FaSchoolCircleExclamation className="h-4 w-4" />
    },
    {
      key: "school-exist",
      titleTop: "Exited School –",
      titleBottom: "Not Boarded Evening Bus",
      inconsistencyType: 6,
      count: attendanceInconsistency.noReturnPickup,
      percentage: getPercentage(attendanceInconsistency.noReturnPickup),
      tagColor: "#EC4899",
      tagBg: "#FCE7F3",
      iconColor: "#DB2777",
      icon: <RxExit className="h-4 w-4" />
    },
    {
      key: "eve-pickup",
      titleTop: "Boarded Evening Bus –",
      titleBottom: "Not Deboarded",
      inconsistencyType: 7,
      count: attendanceInconsistency.noReturnDrop,
      percentage: getPercentage(attendanceInconsistency.noReturnDrop),
      tagColor: "#0EA5E9",
      tagBg: "#E0F2FE",
      iconColor: "#0284C7",
      icon: <FaBusAlt className="h-4 w-4" />
    }
    // {
    //   key: "eve-drop",
    //   titleTop: "Morning Boarding –",
    //   titleBottom: "Evening Deboarding Count Mismatch",
    //   inconsistencyType: 7,
    //   count: attendanceInconsistency.pickupDropMismatchCount,
    //   percentage: getPercentage(
    //     attendanceInconsistency.pickupDropMismatchCount
    //   ),
    //   tagColor: "#4F46E5",
    //   tagBg: "#E0E7FF",
    //   iconColor: "#4338CA",
    //   icon: <GoArrowSwitch className="h-4 w-4" />
    // }
  ];

  return (
    <div className="h-[360px] rounded-xl border bg-neo-white p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="text-base font-semibold">
            Attendance Inconsistency Summary
          </div>
          <div>
            <Popover
              showArrow
              placement="right"
              backdrop="opaque"
              isOpen={isPopoverOpen}
              onOpenChange={open => setIsPopOverOpen(open)}
            >
              <PopoverTrigger>
                <button className="flex h-4 w-4 items-center justify-center rounded-full bg-gray-400">
                  <svg
                    width="5"
                    height="10"
                    viewBox="0 0 5 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M3.92937 2.66603C4.078 2.40593 4.1616 2.10402 4.1616 1.78354C4.1616 0.798879 3.36272 0 2.37806 0C1.39339 0 0.594514 0.798879 0.594514 1.78354C0.594514 2.76821 1.39339 3.56709 2.37806 3.56709C3.04224 3.56709 3.62282 3.2048 3.92937 2.66603ZM1.18903 4.75611H1.78354H2.97257C3.62979 4.75611 4.1616 5.28793 4.1616 5.94514V7.13417V11.8903C4.1616 12.5475 3.62979 13.0793 2.97257 13.0793C2.31535 13.0793 1.78354 12.5475 1.78354 11.8903V8.02594C1.78354 7.53361 1.3841 7.13417 0.891772 7.13417C0.399439 7.13417 0 6.73473 0 6.2424V5.94514C0 5.52713 0.215976 5.15788 0.541101 4.94654C0.726887 4.82578 0.94983 4.75611 1.18903 4.75611Z"
                      fill="white"
                    />
                  </svg>
                </button>
              </PopoverTrigger>
              <PopoverContent>
                <div>
                  <div className="max-w-72 flex-row items-center justify-center p-2">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h2 className="font-bold text-slate-900">
                          Attendance Inconsistency
                        </h2>

                        <p className="mt-2 text-xs font-medium text-slate-500">
                          Highlights cases where a student’s bus attendance and
                          campus attendance records do not align. A student may
                          appear in more than one category.
                        </p>
                      </div>
                    </div>

                    <div className="mt-2">
                      <div className=" flex flex-row gap-5">
                        <div className="flex items-center justify-center">
                          <FaBusAlt className="h-3.5 w-3.5" />
                        </div>
                        <div>
                          <h3 className="text-xs font-bold text-slate-900">
                            Boarded Bus – Not Deboarded at School
                          </h3>
                          <p className="text-xs font-medium text-slate-500">
                            Student boarded the morning bus but did not deboard
                            at the school
                          </p>
                        </div>
                      </div>

                      <div className=" mt-2 flex flex-row gap-5">
                        <div className="flex items-center justify-center">
                          <RiDoorOpenLine className="h-4 w-4" />
                        </div>
                        <div>
                          <h3 className="text-xs font-bold text-slate-900">
                            Deboarded at School – No School Entry
                          </h3>
                          <p className=" text-xs font-medium text-slate-500">
                            Student deboarded from the bus at school but did not
                            enter the school building.
                          </p>
                        </div>
                      </div>

                      <div className=" mt-2 flex flex-row gap-5">
                        <div className="flex items-center justify-center">
                          <TbDoorEnter className="h-4 w-4" />
                        </div>
                        <div>
                          <h3 className=" text-xs font-bold text-slate-900">
                            School Entry Found – No Morning Bus Record
                          </h3>
                          <p className="text-xs font-medium text-slate-500">
                            Student entered school but was not recorded as
                            boarding any bus
                          </p>
                        </div>
                      </div>

                      <div className=" mt-2 flex flex-row gap-5">
                        <div className="flex items-center justify-center">
                          <FaSchoolCircleExclamation className="h-4 w-4" />
                        </div>
                        <div>
                          <h3 className=" text-xs font-bold text-slate-900">
                            Entered School – No School Exit
                          </h3>
                          <p className=" text-xs font-medium text-slate-500">
                            Student entered school but did not exit the school
                            building.
                          </p>
                        </div>
                      </div>

                      <div className=" mt-2 flex flex-row gap-5">
                        <div className="flex items-center justify-center">
                          <RxExit className="h-3.5 w-3.5" />
                        </div>
                        <div>
                          <h3 className=" text-xs font-bold text-slate-900">
                            Exited School – Not Boarded Evening Bus
                          </h3>
                          <p className=" text-xs font-medium text-slate-500">
                            Student exited school but did not board the bus for
                            the evening trip.
                          </p>
                        </div>
                      </div>

                      <div className=" mt-2 flex flex-row gap-5">
                        <div className="flex items-center justify-center">
                          <FaBusAlt className="h-3.5 w-3.5" />
                        </div>
                        <div>
                          <h3 className=" text-xs font-bold text-slate-900">
                            Boarded Evening Bus – Not Deboarded
                          </h3>
                          <p className=" text-xs font-medium text-slate-500">
                            Student boarded the evening bus but did not deboard
                            at their designated stop.
                          </p>
                        </div>
                      </div>

                      {/* <div className=" mt-2 flex flex-row gap-5">
                        <div className="flex items-center justify-center">
                          <GoArrowSwitch className="h-4 w-4" />
                        </div>
                        <div>
                          <h3 className=" text-xs font-bold text-slate-900">
                            Morning Boarding – Evening Deboarding Count Mismatch
                          </h3>
                          <p className=" text-xs font-medium text-slate-500">
                            Student boarded the morning bus but did not deboard
                            at their designated evening stop.
                          </p>
                        </div>
                      </div> */}
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <button
          className="cursor-pointer pr-1"
          title="View"
          onClick={() => openPopup({title: "Attendance Inconsistency Summary"})}
        >
          <EyeIcon size={18} />
        </button>
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <div className="flex h-[calc(100%-28px)] flex-col">
          <div className="mt-1 text-[11px] text-gray-600">
            A user may appear in more than one category.
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
            <div className="flex items-center gap-2 border-r border-slate-200 pr-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-500">
                <div>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 33 33"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M16.5 0C7.3872 0 0 7.3872 0 16.5C0 25.6128 7.3872 33 16.5 33C25.6128 33 33 25.6128 33 16.5C33 7.3872 25.6128 0 16.5 0ZM18.0872 19.5684L18.538 6.47989C18.538 5.49163 17.7999 4.68307 16.8966 4.68307H16.1034C15.2009 4.68307 14.462 5.49163 14.462 6.47989L14.9128 19.5684C14.9128 20.5242 15.6275 21.3061 16.5 21.3061C17.3733 21.3061 18.0872 20.5242 18.0872 19.5684ZM16.5 23.9948C15.3062 23.9948 14.339 24.9629 14.339 26.1559C14.339 27.3497 15.307 28.3169 16.5 28.3169C17.6938 28.3169 18.6618 27.3489 18.6618 26.1559C18.661 24.9629 17.6938 23.9948 16.5 23.9948Z"
                      fill="#EF4444"
                    />
                  </svg>
                </div>
              </div>
              <div>
                <div className="text-lg font-semibold leading-none text-red-500">
                  {attendanceInconsistency.totalInconsistencies}
                </div>
                <div className="mt-1 text-[11px] font-semibold text-slate-700">
                  Total Inconsistencies
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 pl-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 text-orange-500">
                <div>
                  <svg
                    width="25"
                    height="18"
                    viewBox="0 0 36 29"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.1 0C9.29347 0 10.4381 0.477398 11.282 1.32717C12.1259 2.17695 12.6 3.32949 12.6 4.53125C12.6 5.73301 12.1259 6.88555 11.282 7.73533C10.4381 8.5851 9.29347 9.0625 8.1 9.0625C6.90653 9.0625 5.76193 8.5851 4.91802 7.73533C4.07411 6.88555 3.6 5.73301 3.6 4.53125C3.6 3.32949 4.07411 2.17695 4.91802 1.32717C5.76193 0.477398 6.90653 0 8.1 0ZM28.8 0C29.9935 0 31.1381 0.477398 31.982 1.32717C32.8259 2.17695 33.3 3.32949 33.3 4.53125C33.3 5.73301 32.8259 6.88555 31.982 7.73533C31.1381 8.5851 29.9935 9.0625 28.8 9.0625C27.6065 9.0625 26.4619 8.5851 25.618 7.73533C24.7741 6.88555 24.3 5.73301 24.3 4.53125C24.3 3.32949 24.7741 2.17695 25.618 1.32717C26.4619 0.477398 27.6065 0 28.8 0ZM0 16.9186C0 13.5824 2.68875 10.875 6.00187 10.875H8.40375C9.29812 10.875 10.1475 11.0732 10.9125 11.4244C10.8394 11.8322 10.8056 12.257 10.8056 12.6875C10.8056 14.8512 11.7506 16.7939 13.2412 18.125C13.23 18.125 13.2187 18.125 13.2019 18.125H1.19812C0.54 18.125 0 17.5813 0 16.9186ZM22.7981 18.125C22.7869 18.125 22.7756 18.125 22.7587 18.125C24.255 16.7939 25.1944 14.8512 25.1944 12.6875C25.1944 12.257 25.155 11.8379 25.0875 11.4244C25.8525 11.0676 26.7019 10.875 27.5963 10.875H29.9981C33.3112 10.875 36 13.5824 36 16.9186C36 17.5869 35.46 18.125 34.8019 18.125H22.7981ZM12.6 12.6875C12.6 11.2454 13.1689 9.86234 14.1816 8.84261C15.1943 7.82288 16.5678 7.25 18 7.25C19.4322 7.25 20.8057 7.82288 21.8184 8.84261C22.8311 9.86234 23.4 11.2454 23.4 12.6875C23.4 14.1296 22.8311 15.5127 21.8184 16.5324C20.8057 17.5521 19.4322 18.125 18 18.125C16.5678 18.125 15.1943 17.5521 14.1816 16.5324C13.1689 15.5127 12.6 14.1296 12.6 12.6875ZM7.2 27.4877C7.2 23.3189 10.5581 19.9375 14.6981 19.9375H21.3019C25.4419 19.9375 28.8 23.3189 28.8 27.4877C28.8 28.3203 28.1306 29 27.2981 29H8.70187C7.875 29 7.2 28.326 7.2 27.4877Z"
                      fill="#F28A03"
                    />
                  </svg>
                </div>
              </div>
              <div>
                <div className="text-lg font-semibold leading-none text-orange-500">
                  {attendanceInconsistency.affectedStudents}/
                  {attendanceInconsistency.totalStudents}
                </div>
                <div className="mt-1 text-[11px] font-semibold text-slate-700">
                  Affected User
                </div>
              </div>
            </div>
          </div>

          <div className="mt-2 flex-1 space-y-0.5 overflow-y-auto pr-1">
            {detailCards.map(card => (
              <div
                key={card.key}
                className="flex flex-row items-center gap-3 border-b border-slate-100 px-1 py-1"
              >
                <div className="flex items-center">
                  <div
                    className="flex h-8 w-8 items-center justify-center rounded-md p-1"
                    style={{
                      backgroundColor: card.tagBg,
                      color: card.iconColor
                    }}
                  >
                    {card.icon}
                  </div>
                </div>

                <div className="min-w-0 flex-1 pr-0">
                  <div className="flex flex-col text-[11px] font-medium leading-4 text-slate-700">
                    <span className="truncate">{card.titleTop}</span>
                    <span className="truncate">{card.titleBottom}</span>
                  </div>
                </div>

                <div className="flex w-[150px] shrink-0 items-center gap-1">
                  <div className="mr-1 text-right text-xs font-semibold text-slate-800">
                    {card.count}
                  </div>
                  <div className="flex w-[150px] items-center gap-1">
                    <div
                      className="h-1 flex-1 rounded-full"
                      style={{backgroundColor: card.tagBg}}
                    >
                      <div
                        className="h-1 rounded-full"
                        style={{
                          width: `${card.percentage}%`,
                          backgroundColor: card.tagColor
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="w-7 text-right text-xs font-semibold text-slate-700">
                        {card.percentage}%
                      </div>
                      <button
                        type="button"
                        title={`View ${card.titleTop} ${card.titleBottom} details`}
                        onClick={() =>
                          openPopup(
                            card.inconsistencyType
                              ? {
                                  title: "Attendance Inconsistency Summary",
                                  innerFilterDefaultsIicon: {
                                    inconsistencyType: [card.inconsistencyType]
                                  }
                                }
                              : {title: "Attendance Inconsistency Summary"}
                          )
                        }
                      >
                        <EyeIcon size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceInconsistencySummarywidget;
